import { Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { combineLatest, merge, Observable, of } from 'rxjs'
import { RootStoreState } from '../../../root-store/root-index'
import * as fromConfigSelectors from '../../../root-store/config-store/config.selectors'
import { catchError, concatMap, filter, map, switchMap, take } from 'rxjs/operators'
import { EntityConfiguration, EntityFilterTile } from '../models/entity-configuration'
import { root } from '../../permissions/permissions'
import { JmeleonActionsPermissionService } from '../../permissions/services/jmeleon-actions-permission.service'
import { Field } from '../models/field'
import { DtoType } from '../../../root-store/page-state-store/page-state.state'

/**
 * Provides the locally stored entity-configurations.
 */
@Injectable({
  providedIn: 'root'
})
export class EntityConfigService {
  /** the locally stored entity configurations */
  configurations$: Observable<Record<string, EntityConfiguration>>
  hasConfigurations$: Observable<boolean>
  private root = root

  constructor(
    private store$: Store<RootStoreState.State>,
    private japs: JmeleonActionsPermissionService
  ) {
    this.configurations$ = this.store$.pipe(select(fromConfigSelectors.selectConfigs))
    this.hasConfigurations$ = this.configurations$.pipe(map((configs) => !!configs && Object.keys(configs).length > 0))
  }

  /** gets the configuration for the given entity type */
  getConfig$ = (entityType: string): Observable<EntityConfiguration> =>
    // this.configurations$.pipe(
    //   map(configs => configs[entityType])
    // )
    this.store$.pipe(select(fromConfigSelectors.selectConfigForType(entityType)))

  /** gets the view-type for the given entity type */
  getEntityFilterTiles$ = (entityType: string): Observable<EntityFilterTile[]> => {
    // get entityFilterTiles from config
    return this.getConfig$(entityType).pipe(
      take(1),
      map((config) => {
        if (config?.entityFilterTiles) {
          return config.entityFilterTiles
        } else {
          return []
        }
      })
    )
  }

  /** gets the view-type for the given entity type */
  getViewType$ = (entityType: string): Observable<string> =>
    this.getConfig$(entityType).pipe(
      map((config) => config['viewType'] ?? 'singlePage') // same as: config['viewType'] ? config['viewType'] : 'singlePage'
    )

  /** gets all fields that may be edited
   * option: checkForReadPermissions: when set to true, only those fields will be returned, where the user has read and write permissions.
   *          (to a)
   */
  getEditableFields$ = (entityType: string, options?: { checkForReadPermission?: boolean }): Observable<Field[]> => {
    const editableFields$ = this.getConfig$(entityType).pipe(
      filter((config) => !!config?.fields),
      concatMap((config) => {
        // showOnUpdate shall not be checked here because this is only the decision whether to show or not in update dialog
        const filteredFields = config.fields.filter(
          (field) => field.editable === true && field.field !== config.orderedColumn
        )

        if (filteredFields.length === 0) {
          console.log('filteredFields list for editable fields is empty: ', entityType)
          return of([])
        }
        const actions: string[] = [root.dto.$dtoType.write]
        if (options?.checkForReadPermission) {
          actions.push(root.dto.$dtoType.read)
        }

        const userHasWritePermissionForEntity$ = this.japs
          .userHasPermissionForAllActions(actions, { $dtoType: entityType })
          .pipe()

        const fieldsForEntityLevelWritePermission$ = userHasWritePermissionForEntity$.pipe(
          filter((hasPermission) => !hasPermission),
          map((_) => [])
        )

        let filteredFieldPermissions = filteredFields.map((f) =>
          root.dto.$dtoType.$dtoField.write.replace('$dtoType', entityType).replace('$dtoField', f.field)
        )

        if (options?.checkForReadPermission) {
          // for every field we check also the read-permission. only fields with read and write permisson will be returned as editable fields.
          const readPermissions = filteredFieldPermissions.map((p) => p.replace('write', 'read'))
          filteredFieldPermissions = filteredFieldPermissions.concat(readPermissions)
        }

        const fieldsForFieldLevelWritePermissions$ = userHasWritePermissionForEntity$.pipe(
          filter((hasPermission) => hasPermission),
          map((_) => filteredFieldPermissions),
          switchMap((fields) => this.japs.userHasPermissionForActions(fields)),
          map((permissionDict) =>
            filteredFieldPermissions
              //.filter(perm => permissionDict[perm])
              .filter((perm) => {
                const result =
                  perm.includes('write') && permissionDict[perm] && permissionDict[perm.replace('write', 'read')]
                return result
              })
              // get the field name from the string 'root.dto.$dtoType.$dtoField.write' with replaced variables ($dtoField)
              .map((perm) => perm.split('.').slice(-2, -1)[0])
              // get field with field name
              .map((fieldName) => filteredFields.find((f) => f.field === fieldName))
          )
        )

        return merge(fieldsForEntityLevelWritePermission$, fieldsForFieldLevelWritePermissions$)
      }),
      catchError((err) => {
        console.error('(permissions in editable fields) err: ', err)
        return of([])
      })
    )

    return editableFields$
  }

  /** gets all fields that are required to create an entity */
  getCreationFields$ = (entityType: string): Observable<Field[]> => {
    const creationFields$: Observable<Field[]> = this.getConfig$(entityType).pipe(
      filter((config) => !!config?.fields),
      concatMap((config) => {
        const filteredFields = config.fields.filter((field) => field.showOnCreate || field.mandatory)
        if (filteredFields.length > 0) {
          return this.japs
            .userHasPermissionForAction(root.dto.$dtoType.create, { $dtoType: entityType })
            .pipe(map((createPermission) => (createPermission ? filteredFields : [])))
        } else {
          return of([])
        }
      })
      //tap(result => console.log('creationFields: ', result))
    )

    return creationFields$
  }

  /** gets all fields that are to be displayed for the current user */
  // getEditableFields$ & getVisibleFields$ share most of their code and could be generalized
  getVisibleFields$ = (entityType: string, includeUpdateFields = false): Observable<Field[]> => {
    console.log('(perm) getting visible fields for ', entityType)

    const visibleFields$ = this.getConfig$(entityType).pipe(
      filter((config) => !!config?.fields),
      concatMap((config) => {
        const filteredFields = config.fields.filter(
          (field) => field.visible || (includeUpdateFields && field.showOnUpdate)
        )

        if (filteredFields.length === 0) {
          console.log('filteredFields list for visible fields is empty: ', entityType)
          return of([])
        }

        const userHasReadPermissionForEntity$ = this.japs
          .userHasPermissionForAction(root.dto.$dtoType.read, { $dtoType: entityType })
          .pipe()

        const fieldsForEntityLevelReadPermission$ = userHasReadPermissionForEntity$.pipe(
          filter((hasPermission) => !hasPermission),
          map((_) => [])
        )

        const filteredFieldPermissions = filteredFields.map((f) =>
          root.dto.$dtoType.$dtoField.read.replace('$dtoType', entityType).replace('$dtoField', f.field)
        )

        const fieldsForFieldLevelReadPermissions$ = userHasReadPermissionForEntity$.pipe(
          filter((hasPermission) => hasPermission),
          map((_) => filteredFieldPermissions),
          switchMap((fields) => this.japs.userHasPermissionForActions(fields)),
          map((permissionDict) =>
            filteredFieldPermissions
              .filter((perm) => permissionDict[perm])
              // get the field name from the string 'root.dto.$dtoType.$dtoField.read' with replaced variables ($dtoField)
              .map((perm) => perm.split('.').slice(-2, -1)[0])
              // get field with field name
              .map((fieldName) => filteredFields.find((f) => f.field === fieldName))
          )
        )
        return merge(fieldsForEntityLevelReadPermission$, fieldsForFieldLevelReadPermissions$)
      })
    )

    return visibleFields$
  }

  mapIntoElements$ = (obs: Observable<any>): Observable<any> => obs.pipe(map((e) => e))

  // what is with exclude panels???
  getDtoType$ = (entityType: string): Observable<DtoType> =>
    combineLatest([
      this.getConfig$(entityType),
      this.getVisibleFields$(entityType, true),
      this.getEditableFields$(entityType, { checkForReadPermission: true }),
      this.getCreationFields$(entityType)
    ]).pipe(
      map(([config, visibleField, editableFields, creationFields]) => {
        const result = <DtoType>{
          config,
          headerFields: visibleField,
          editableFields,
          creationFields,
          excludedPanels: ['']
        }
        return result
      })
    )
}
